import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AutoComplete, Input } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import {
  LDS_AUTOCMPLETE_CITIES,
  LDS_AUTOCOMPLETE_MORE,
} from "../../../../../assets/Data/LdsAirportsAutocomplete";
import {
  DYNAMIC_PACKAGES,
  PLANES,
  useSearch,
} from "../../../../../contexts/SearchContext";

/** LDS getActiveDestinations uses dealType "Flights" for flight-only inventory; UI tab PLANES must map to it. */
function dealTypeForLdsAutocomplete(searchTab) {
  if (searchTab === DYNAMIC_PACKAGES || searchTab === PLANES) return "Flights";
  return searchTab;
}

export const parseLocationToString = (l) => {
  if (!l || typeof l !== "object") return "";
  const cityCode = String(l.CITYCODE ?? "").trim().toUpperCase();
  const airportCode = String(l.AIRPORTCODE ?? "").trim();
  if (cityCode === "ALL" || airportCode === "ALL") {
    return String(l.COUNTRYNAME_HEB || l.COUNTRYNAME || "כל היעדים").trim();
  }
  if (airportCode === "") {
    const label = String(l.COUNTRYNAME_HEB || l.COUNTRYNAME || "").trim();
    return label || String(l.fullname || l.CITYNAME_HEB || "").trim();
  }
  const countryHeb = String(l.COUNTRYNAME_HEB ?? "").trim();
  const cityHeb = String(l.CITYNAME_HEB ?? "").trim();
  if (countryHeb || cityHeb) {
    return `(${airportCode}) ${[countryHeb, cityHeb].filter(Boolean).join(" - ")}`.trim();
  }
  const city = String(l.CITYNAME ?? "").trim();
  const country = String(l.COUNTRYNAME ?? "").trim();
  return [city, country].filter(Boolean).join(" - ");
};

const AutoCompleteInput = ({
  saveLocationSelection,
  location = "",
  isOrigin,
}) => {
  const { categories, destinations, searchTab } = useSearch();
  const [currentValue, setCurrentValue] = useState(
    location ? parseLocationToString(location) : ""
  );
  const [cities, setCities] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const inputRef = useRef();

  useEffect(() => {
    setCurrentValue(location ? parseLocationToString(location) : "");
    // Reset isOpen when location changes (after search)
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    console.log("AutocompleteinputLDS: useEffect triggered with:", {
      searchTab,
      categories,
      destinations: destinations?.length || 0
    });
    
    const st = dealTypeForLdsAutocomplete(searchTab);
    console.log("AutocompleteinputLDS: searchTab type:", st);
    
    const cities = destinations
      .filter(
        (dest) =>
          dest.dealType === st &&
          (categories.length === 0 ||
            (categories.includes("ALL") &&
              (dest.subject === "ALL" || !dest.subject)) ||
            categories.includes(dest.subject))
      )
      .map((city) => {
        const hebCity = LDS_AUTOCMPLETE_CITIES.find(
          (ldsCity) => ldsCity.AIRPORTCODE === city.destCity
        );
        return {
          COUNTRYNAME_HEB: hebCity?.COUNTRYNAME_HEB || city.destCountryName,
          CITYNAME_HEB: hebCity?.CITYNAME_HEB || city.destCountryName,
          AIRPORTCODE: city.destCity,
          AIRPORTNAME: hebCity?.AIRPORTNAME || "",
          CITYCODE: city.destCity,
          CITYNAME: city.destCityName,
          COUNTRYCODE: hebCity?.COUNTRYCODE || "",
          COUNTRYNAME: city.destCountryName,
        };
      });

    console.log("AutocompleteinputLDS: filtered cities:", cities);

    const hebCities = cities.reduce((acc, city) => {
      if (!acc.find((c) => c.CITYCODE === city.CITYCODE)) {
        acc.push(city);
      }
      return acc;
    }, []);

    console.log("AutocompleteinputLDS: hebCities before LDS_AUTOCOMPLETE_MORE:", hebCities);

    if (searchTab !== DYNAMIC_PACKAGES && searchTab !== PLANES)
      for (let i = 0; i < LDS_AUTOCOMPLETE_MORE.length; i++)
        hebCities.unshift(LDS_AUTOCOMPLETE_MORE[i]);

    console.log("AutocompleteinputLDS: final cities:", hebCities);
    setCities(hebCities);
    // Reset isOpen when cities list updates (after search results)
    setIsOpen(false);
  }, [searchTab, categories, destinations]);

  // Focus the input on mobile when it's rendered
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        // For Ant Design Input, try to get the actual input element
        if (inputRef.current) {
          const inputElement = inputRef.current.input || inputRef.current;
          if (inputElement && typeof inputElement.focus === 'function') {
            inputElement.focus();
            // On mobile, clicking also helps trigger the keyboard
            if (inputElement.click && typeof inputElement.click === 'function') {
              inputElement.click();
            }
          }
        }
      }, 150);
    }
  }, []);

  // Helper function to get flag URL from country code
  const getFlagUrl = (countryCode) => {
    if (!countryCode) return null;
    return `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`;
  };

  // Ant Design expects options as [{ value, label, ... }]
  const getAntdOptions = () => {
    // If cities is empty, use LDS_AUTOCOMPLETE_MORE as fallback
    const citiesToUse = cities.length > 0 ? cities : LDS_AUTOCOMPLETE_MORE;

    // If currentValue is empty, show all options (for dropdown to open properly)
    const filterValue = currentValue ? currentValue.toLowerCase().trim() : "";

    return citiesToUse
      .filter((city) => {
        // If no filter value, show all options
        if (!filterValue) return true;
        
        // Otherwise filter by matching any field
        return Object.values(city).some((value) =>
          value
            ? value
                .toString()
                .toLowerCase()
                .includes(filterValue)
            : false
        );
      })
      .map((option) => {
        const flagUrl = getFlagUrl(option.COUNTRYCODE);
        let label;
        if (option.CITYCODE === "ALL") {
          label = (
            <div className="autoComplete-input-option-row">
              {flagUrl && (
                <img 
                  src={flagUrl} 
                  alt={option.COUNTRYCODE || ""} 
                  className="autoComplete-input-flag"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>{`${option.COUNTRYNAME_HEB} - ${option.COUNTRYNAME}`}</div>
            </div>
          );
        } else {
          label = (
            <div className="autoComplete-input-option-row">
              {flagUrl && (
                <img 
                  src={flagUrl} 
                  alt={option.COUNTRYCODE || ""} 
                  className="autoComplete-input-flag"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="autoComplete-input-option-content">
                {option?.COUNTRYNAME_HEB && option?.CITYNAME_HEB ? (
                  <div>
                    {`(${option.AIRPORTCODE}) ${option.COUNTRYNAME_HEB} - ${option.CITYNAME_HEB}`}
                  </div>
                ) : null}
                <div>{`${option.CITYNAME} - ${option.COUNTRYNAME}`}</div>
              </div>
            </div>
          );
        }
        return {
          value: parseLocationToString(option),
          label,
          optionData: option,
        };
      });
  };

  const wrapperRef = useRef(null);

  return (
    <div className="autoComplete-input-box-wrapper" ref={wrapperRef}>
      <AutoComplete
        options={getAntdOptions()}
        value={currentValue}
        style={{ width: "100%" }}
        allowClear
        open={isOpen}
        onFocus={() => {
          // Always try to open dropdown on focus, even if options might be empty initially
          // The options will be populated from cities or LDS_AUTOCOMPLETE_MORE
          const options = getAntdOptions();
          // Open if there are options OR if cities list exists (will show all cities)
          if (options.length > 0 || cities.length > 0 || LDS_AUTOCOMPLETE_MORE.length > 0) {
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          // Delay to allow selection to complete
          setTimeout(() => setIsOpen(false), 200);
        }}
        getPopupContainer={() => {
          // Always render in body to ensure dropdown appears above everything
          return document.body;
        }}
        onChange={(value, option) => {
          setCurrentValue(value);
          // Open dropdown when user starts typing
          if (value && value.length > 0) {
            setIsOpen(true);
          }
          // If user selects from dropdown, option will be defined
          if (option && option.optionData) {
            saveLocationSelection(option.optionData);
          }
        }}
        onSelect={(value, option) => {
          setCurrentValue(value);
          setIsOpen(false);
          if (option && option.optionData) {
            saveLocationSelection(option.optionData);
          }
        }}
        filterOption={false} // Already filtered in getAntdOptions
        popupMatchSelectWidth={false}
        autoClearSearchValue={false}
      >
        <Input
          ref={inputRef}
          className="autoComplete-input-box"
          placeholder={
            !isOrigin
              ? t("hotelsearchbar.selectdestination")
              : t("hotelsearchbar.selectorigin")
          }
          prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
        />
      </AutoComplete>
    </div>
  );
};

export default AutoCompleteInput;
